import { Skeleton } from '@doccareplus/ui';

/**
 * Loading placeholder for the billing page. A `role="status"` region (with
 * an sr-only label) wrapping `aria-hidden` shimmer primitives, so assistive
 * tech announces "loading" once instead of reading every block. Mirrors the
 * real `grid-cols-1 lg:grid-cols-3` split: a balance card and an invoice
 * history list.
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** The current-balance panel: centered label, amount, and note. */
function BalanceCardSkeleton() {
  return (
    <div
      className="h-fit rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-5 text-center shadow-[var(--shadow-1)] lg:col-span-1"
      aria-hidden
    >
      <Skeleton className="mx-auto h-3 w-2/5" />
      <Skeleton className="mx-auto my-2 h-8 w-1/2" />
      <Skeleton className="mx-auto h-3 w-1/3" />
    </div>
  );
}

/** A single invoice-history row: number/date on the left, amount/status on the right. */
function InvoiceRowSkeleton() {
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        <Skeleton className="h-3.5 w-14" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </div>
  );
}

/** The full billing page skeleton — balance panel plus invoice history list. */
export function BillingPageSkeleton({ label, rows = 4 }: { label: string; rows?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <BalanceCardSkeleton />
        <div className="flex flex-col gap-3 lg:col-span-2">
          <Skeleton className="h-3 w-24" aria-hidden />
          {Array.from({ length: rows }).map((_, i) => (
            <InvoiceRowSkeleton key={i} />
          ))}
        </div>
      </div>
    </LoadingRegion>
  );
}
